import { useEffect, useRef, useCallback } from 'react';

interface UseInactivityTimeoutOptions {
  timeoutMs: number;
  warningMs: number;
  onWarning: () => void;
  onTimeout: () => void;
  /** When true, the timer is suspended — no warning, no timeout. */
  paused?: boolean;
}

export function useInactivityTimeout({
  timeoutMs,
  warningMs,
  onWarning,
  onTimeout,
  paused = false,
}: UseInactivityTimeoutOptions) {
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onWarningRef = useRef(onWarning);
  const onTimeoutRef = useRef(onTimeout);
  const pausedRef = useRef(paused);

  useEffect(() => { onWarningRef.current = onWarning; }, [onWarning]);
  useEffect(() => { onTimeoutRef.current = onTimeout; }, [onTimeout]);

  const resetTimer = useCallback(() => {
    if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
    if (warningIdRef.current) clearTimeout(warningIdRef.current);

    // Suspended while a run is active — do not schedule warning/timeout.
    if (pausedRef.current) return;

    const warningTime = timeoutMs - warningMs;

    warningIdRef.current = setTimeout(() => {
      // Defensive: a run may have started after this was scheduled.
      if (pausedRef.current) return;
      onWarningRef.current();
      
      timeoutIdRef.current = setTimeout(() => {
        if (pausedRef.current) return;
        onTimeoutRef.current();
      }, warningMs);
    }, warningTime);
  }, [timeoutMs, warningMs]);

  // When a run starts: clear pending timers (resetTimer no-ops while paused).
  // When a run ends: start a fresh full window.
  useEffect(() => {
    pausedRef.current = paused;
    resetTimer();
  }, [paused, resetTimer]);

  useEffect(() => {
    resetTimer();

    const events = [
      'mousemove',
      'mousedown',
      'keypress',
      'keydown',
      'scroll',
      'touchstart',
      'touchmove',
      'click',
    ];

    let debounceId: ReturnType<typeof setTimeout> | null = null;
    const handleActivity = () => {
      if (debounceId) clearTimeout(debounceId);
      debounceId = setTimeout(() => {
        resetTimer();
      }, 500); 
    };

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
      if (warningIdRef.current) clearTimeout(warningIdRef.current);
      if (debounceId) clearTimeout(debounceId);
      
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [timeoutMs, warningMs, resetTimer]);

  return { resetTimer };
}
